import { Injectable, inject } from '@angular/core';
import { UserApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/user/user-api';
import { NativeBridgeService } from '../webview/native-bridge.service';
import { WebViewAction } from '../../enums/webview-action';
import { PosthogService } from './posthog.service';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Au plus une demande tous les 120 jours : Apple montre la feuille 3 fois par an au plus, Google tient un quota. */
export const REVIEW_MIN_INTERVAL_MS = 120 * DAY_MS;
/** Pas de demande à qui découvre l'app : un compte d'au moins 14 jours. */
export const REVIEW_MIN_ACCOUNT_AGE_MS = 14 * DAY_MS;
/** Réponses de Willy reçues avant de demander un avis. */
export const REVIEW_WILLY_ANSWERS = 3;

const REQUESTED_AT_KEY = 'vw-review-requested-at';
const WILLY_ANSWERS_KEY = 'vw-review-willy-answers';

export type ReviewTrigger = 'harvest' | 'willy' | 'feedback';

/**
 * L'avis des magasins, demandé au bon moment (CR-06, MKT-16).
 *
 * Juste après un moment où l'app a rendu service : une récolte notée, une 3e
 * réponse de Willy, un avis « sourire » dans « Donne ton avis ». Jamais après
 * un push, un paywall ou une erreur : ces déclencheurs n'existent pas ici. Le
 * système décide seul s'il montre sa feuille (coque 1.6, canal `review`) ; un
 * ancien binaire ou un navigateur n'est jamais sollicité.
 */
@Injectable({ providedIn: 'root' })
export class StoreReviewService {
  private readonly bridge = inject(NativeBridgeService);
  private readonly posthog = inject(PosthogService);
  private readonly userApi = new UserApi();
  /** Une vérification est en cours : deux gestes rapprochés ne demandent qu'une fois. */
  private checking = false;

  /** Une récolte vient d'être notée. */
  afterHarvestLogged(): Promise<void> {
    return this.maybeAsk('harvest');
  }

  /** Willy vient de répondre, sans erreur ni refus : à partir de la 3e réponse, on demande. */
  afterWillyAnswer(): Promise<void> {
    const answers = Number(readItem(WILLY_ANSWERS_KEY) ?? 0) + 1;
    writeItem(WILLY_ANSWERS_KEY, String(answers));
    return answers >= REVIEW_WILLY_ANSWERS ? this.maybeAsk('willy') : Promise.resolve();
  }

  /** Un avis « sourire » vient d'être envoyé. « Bof » et « mauvais » restent chez nous. */
  afterHappyFeedback(): Promise<void> {
    return this.maybeAsk('feedback');
  }

  private async maybeAsk(trigger: ReviewTrigger): Promise<void> {
    if (this.checking || !this.bridge.has(WebViewAction.review) || this.askedRecently()) return;
    this.checking = true;
    try {
      const user = await this.userApi.getCurrentUser();
      const registeredAt = new Date(user?.registrationDate ?? NaN).getTime();
      if (!(Date.now() - registeredAt >= REVIEW_MIN_ACCOUNT_AGE_MS)) return;
      if (!this.bridge.requestReview()) return;
      writeItem(REQUESTED_AT_KEY, String(Date.now()));
      // La coque ne dit pas si la feuille s'est montrée : c'est la demande qui est comptée.
      this.posthog.capture('review_prompt_shown', { trigger });
    } catch {
      // Sans profil lisible, on ne demande rien : ce sera pour le prochain geste.
    } finally {
      this.checking = false;
    }
  }

  private askedRecently(): boolean {
    const at = Number(readItem(REQUESTED_AT_KEY) ?? 0);
    return Number.isFinite(at) && Date.now() - at < REVIEW_MIN_INTERVAL_MS;
  }
}

// Stockage local à l'abri : une WebView ou un navigateur privé peut le refuser.
function readItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Sans stockage, l'écart de 120 jours ne tient pas ; le système garde son propre plafond.
  }
}
